"use client";

import { useFollowKeyword } from "@/hooks/use-follow-keyword";
import { useLoginGate } from "@/components/login-gate-dialog";
import { Button } from "@/components/ui/button";
import { Star, Loader2 } from "lucide-react";
import { useState } from "react";

interface CelebrityFollowButtonProps {
  name: string;
  compact?: boolean;
  className?: string;
}

/**
 * 名人一键关注按钮。
 * 基于 useFollowKeyword，关注 = 订阅名人名作为关键词。
 */
export function CelebrityFollowButton({
  name,
  compact = false,
  className,
}: CelebrityFollowButtonProps) {
  const { following, toggle, loading } = useFollowKeyword(name);
  const [toggling, setToggling] = useState(false);

  const { requireLogin, dialog: loginDialog } = useLoginGate({
    title: "登录后关注名人",
    description: "登录即可关注名人，新动态第一时间邮件提醒。关注越多，该明星的热搜追踪和事件解读资源越充足。",
    icon: <Star className="w-5 h-5" />,
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (toggling) return;
    requireLogin(async () => {
      setToggling(true);
      await toggle();
      setToggling(false);
    });
  };

  const busy = loading || toggling;

  if (compact) {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          disabled={busy}
          className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium transition-colors disabled:opacity-50 ${
            following
              ? "border border-primary/30 text-primary hover:bg-primary/10"
              : "bg-primary text-primary-foreground hover:bg-primary/90"
          } ${className || ""}`}
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Star className={`h-2.5 w-2.5 ${following ? "fill-primary" : ""}`} />
          )}
          {following ? "已关注" : "关注"}
        </button>
        {loginDialog}
      </>
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant={following ? "outline" : "default"}
        onClick={handleClick}
        disabled={busy}
        className={className}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin mr-1" />
        ) : (
          <Star className={`h-4 w-4 mr-1 ${following ? "fill-primary" : ""}`} />
        )}
        {following ? "已关注" : "关注"}
      </Button>
      {loginDialog}
    </>
  );
}
